/**
 * MaturityReferencePage — companion to MaturityHealthPage.
 *
 * Sister to LEIReferencePage / ROEReferencePage / PEReferencePage:
 * pure narrative explainer that frames Learning Maturity as a
 * *multiplier* across the LEI → PE → ROE → KPI → ROI value chain.
 * No live data; the Health page consumes scores, this page teaches
 * the model.
 *
 * Visual approach mirrors ROEReferencePage:
 *   - Header + Back
 *   - Big Picture / Value Chain banner
 *   - Pillar deep-dives (5 cards, one per pillar)
 *   - Levels grid (Emerging → Optimized)
 *   - ROI Leakage formula card
 *   - "Where maturity impacts the system" stage list
 *   - Primary Constraint callout
 *   - How to Improve (4 steps)
 *   - Key Takeaways
 *   - Cross-references
 *
 * Indigo palette to differentiate from ROE (teal), LEI (blue), PE
 * (cyan/purple) — keeps the family of refs visually distinct.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  AlertTriangle,
  Activity,
  Layers,
  Target,
  Gauge,
  Users,
  Briefcase,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Compass,
  CheckCircle2,
  BookOpen,
  Sparkles,
  Download,
  Zap,
  Droplet,
  FileText,
  Code2,
  Copy,
  Check,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import api from '../lib/api';
import RelatedReferences from '../components/reference/RelatedReferences';
import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import { getLightSectionTheme } from '../lib/sectionThemes';


// Whitepaper PDF (uploaded by user via the Emergent assets bucket).
// Hard-coded URL matches the pattern used in LEIReferencePage so swap
// is a one-line edit when an updated version drops.
const GOVERNANCE_WHITEPAPER_URL = 'https://customer-assets.emergentagent.com/job_921eb9ff-1a80-47fd-afad-b9e5c495b72e/artifacts/c35jm42p_L_D%20Strategy%20-%20How%20to%20Create%20an%20Effective%20Learning%20Content%20Governance%20Framework_Version_1.pdf';


// Visual constants used across multiple sections; keeping them at the
// top makes palette changes a one-line edit later.
const VALUE_CHAIN = [
  { label: 'Investment', desc: 'Learning\nspend', color: 'bg-slate-600' },
  { label: 'LEI', desc: 'Learning\nEffectiveness', color: 'bg-blue-600' },
  { label: 'PE', desc: 'Workforce\nCapability', color: 'bg-purple-600' },
  { label: 'ROE', desc: 'Transfer\nValidation', color: 'bg-teal-600' },
  { label: 'KPI', desc: 'Business\nPerformance', color: 'bg-emerald-600' },
  { label: 'ROI', desc: 'Financial\nReturn', color: 'bg-amber-600' },
];

// Per-stage retention/amplification factors keyed by maturity band.
// "Investment" is always the 1.0x baseline. Each subsequent stage
// applies a multiplier to the running value — at low maturity the
// chain leaks badly, at optimized maturity each stage *amplifies*
// the previous stage's value (capability compounds into outcomes).
//
// Numbers are illustrative but anchored in the Maturity Factor
// formula already used elsewhere in this page (Potential × Maturity
// = Realized) so the final cumulative figure reads cleanly as a
// percentage of potential ROI.
const MATURITY_BANDS = [
  {
    key: 'low',
    label: 'Low Maturity',
    sublabel: 'Emerging · 0–39',
    factor: 0.35,
    chipBg: 'bg-rose-100',
    chipText: 'text-rose-700',
    chipBorder: 'border-rose-200',
    activeBg: 'bg-rose-600',
    activeText: 'text-white',
    haloRing: 'ring-rose-400/40',
    deltaTone: 'text-rose-600',
    icon: Droplet,
    headline: 'Heavy ROI leakage',
    // retention = how much value carries forward from the previous
    // stage. <1 = leakage, >1 = amplification.
    retention: { LEI: 0.75, PE: 0.70, ROE: 0.55, KPI: 0.80, ROI: 0.85 },
  },
  {
    key: 'medium',
    label: 'Managed Maturity',
    sublabel: 'Managed · 60–79',
    factor: 0.70,
    chipBg: 'bg-amber-100',
    chipText: 'text-amber-700',
    chipBorder: 'border-amber-200',
    activeBg: 'bg-amber-500',
    activeText: 'text-white',
    haloRing: 'ring-amber-400/40',
    deltaTone: 'text-amber-600',
    icon: Activity,
    headline: 'Partial value capture',
    retention: { LEI: 0.92, PE: 0.90, ROE: 0.85, KPI: 0.95, ROI: 1.05 },
  },
  {
    key: 'high',
    label: 'Optimized Maturity',
    sublabel: 'Optimized · 80–100',
    factor: 1.0,
    chipBg: 'bg-emerald-100',
    chipText: 'text-emerald-700',
    chipBorder: 'border-emerald-200',
    activeBg: 'bg-emerald-600',
    activeText: 'text-white',
    haloRing: 'ring-emerald-400/40',
    deltaTone: 'text-emerald-600',
    icon: Zap,
    headline: 'Compounding amplification',
    retention: { LEI: 1.05, PE: 1.08, ROE: 1.12, KPI: 1.15, ROI: 1.20 },
  },
];

// Walks the chain once and returns the cumulative running value at
// each step. Investment is anchored at 1.00 so downstream numbers
// read as a multiplier of original spend.
const computeChainValues = (band) => {
  let running = 1.0;
  return VALUE_CHAIN.map((step) => {
    if (step.label === 'Investment') {
      return { ...step, mult: null, cumulative: running };
    }
    const m = band.retention[step.label] ?? 1;
    running = running * m;
    return { ...step, mult: m, cumulative: running };
  });
};

const PILLARS = [
  {
    name: 'Access & Engagement',
    weight: '20%',
    icon: Users,
    accent: 'blue',
    summary: 'Participation, reach, and voluntary learning behavior across the organization.',
    why: 'No engagement = no impact. Learning must be accessed before it can create value.',
  },
  {
    name: 'Performance & Outcomes',
    weight: '30%',
    icon: Target,
    accent: 'emerald',
    summary: 'Knowledge acquisition, assessment performance, and learning quality.',
    why: 'Capability begins with effective learning. Poor outcomes limit downstream impact.',
  },
  {
    name: 'Capability & Readiness',
    weight: '20%',
    icon: Briefcase,
    accent: 'purple',
    summary: 'Skill application readiness, proficiency levels, and workforce preparedness.',
    why: 'Learning must translate into usable capability to influence performance.',
  },
  {
    name: 'Operational Efficiency',
    weight: '20%',
    icon: Gauge,
    accent: 'amber',
    summary: 'Delivery scalability, cost efficiency, and execution consistency.',
    why: 'Inefficient systems reduce ROI and limit scale of impact.',
  },
  {
    name: 'Business Impact',
    weight: '10%',
    icon: DollarSign,
    accent: 'rose',
    summary: 'Alignment to business goals, KPI influence, and value realization.',
    why: 'Learning must connect to business outcomes to justify investment.',
  },
];

// Tailwind doesn't safely interpolate dynamic colour names at build
// time, so we hand-pick the four classes we need per accent. Adding
// a colour later means adding one row here.
const ACCENT_CLASSES = {
  blue: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-700' },
  emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-700' },
  purple: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', badge: 'bg-purple-100 text-purple-700' },
  amber: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-700' },
  rose: { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-700', badge: 'bg-rose-100 text-rose-700' },
};

const LEVELS = [
  {
    band: 'Emerging',
    range: '0–39',
    color: 'bg-rose-500',
    summary: 'Learning is activity-driven with limited structure or measurement.',
    risk: 'High ROI leakage. Learning rarely translates into performance.',
  },
  {
    band: 'Developing',
    range: '40–59',
    color: 'bg-amber-500',
    summary: 'Basic processes exist with partial measurement and inconsistent alignment.',
    risk: 'Moderate-to-high leakage. Impact is inconsistent and difficult to predict.',
  },
  {
    band: 'Managed',
    range: '60–79',
    color: 'bg-sky-500',
    summary: 'Structured, measurable, and repeatable learning processes are in place.',
    risk: 'Moderate leakage. Strong foundations, but optimization opportunities remain.',
  },
  {
    band: 'Optimized',
    range: '80–100',
    color: 'bg-emerald-600',
    summary: 'Learning is predictive, fully aligned to business strategy, and continuously improved.',
    risk: 'Minimal leakage. High confidence in ROI realization.',
  },
];

const STAGE_IMPACT = [
  {
    stage: 'LEI',
    title: 'Learning Effectiveness',
    impact: 'Low maturity leads to weak measurement, poor engagement, and limited learning outcomes.',
  },
  {
    stage: 'PE',
    title: 'People Engine Capacity',
    impact: 'Capability is developed but not consistently applied due to lack of readiness or support.',
  },
  {
    stage: 'ROE',
    title: 'Return on Expectation',
    impact: 'The largest point of value loss. Learning does not translate into sustained behavior change.',
    isLargest: true,
  },
  {
    stage: 'KPI',
    title: 'KPI Impact',
    impact: 'Business improvements are inconsistent, delayed, or unmeasurable.',
  },
  {
    stage: 'ROI',
    title: 'Financial Return',
    impact: 'Financial return is significantly lower than potential due to system inefficiencies.',
  },
];

const IMPROVE_STEPS = [
  { n: 1, title: 'Identify', body: 'Understand where ROI leakage is occurring across your value chain.' },
  { n: 2, title: 'Diagnose', body: 'Pinpoint the weakest maturity pillar driving performance gaps.' },
  { n: 3, title: 'Prioritize', body: 'Focus on the highest-impact constraint first — not all improvements are equal.' },
  { n: 4, title: 'Operationalize', body: 'Embed governance, measurement, and accountability into your learning system.' },
];

const TAKEAWAYS = [
  'Learning Maturity is a multiplier, not a measure',
  'Most value is lost after learning occurs (transfer gap)',
  'Improving maturity increases ROI without increasing spend',
  'The highest leverage point is governance and alignment',
  'The fastest path to impact is fixing your primary constraint',
];


export default function MaturityReferencePage() {
  const navigate = useNavigate();

  // Default to "Low Maturity" so the first impression is the
  // leakage story — that's the part most users underestimate.
  // We override this once we've fetched the org's actual score so
  // the diagram lands on *their* band by default ("this is where
  // you stand"), keeping the teaching aid personalized.
  const [maturityKey, setMaturityKey] = useState('low');
  const [orgScore, setOrgScore] = useState(null); // 0–100 once loaded
  const [orgBandLabel, setOrgBandLabel] = useState(null); // Emerging/.../Optimized
  // Has the user manually overridden the default? Once true we stop
  // auto-snapping to the org band so a returning visitor doesn't get
  // their selection wiped if `loadData` fires again.
  const [manualOverride, setManualOverride] = useState(false);
  // Tracks the brief "Copied!" affirmation on the seeder command button.
  const [copiedCmd, setCopiedCmd] = useState(false);

  const SEEDER_COMMAND = 'cd backend && python scripts/maturity_blueprint_importer.py';

  const handleCopyCommand = async () => {
    try {
      await navigator.clipboard.writeText(SEEDER_COMMAND);
      setCopiedCmd(true);
      toast.success('Seeder command copied to clipboard');
      setTimeout(() => setCopiedCmd(false), 2000);
    } catch {
      toast.error('Could not copy — select the command manually');
    }
  };

  useEffect(() => {
    let cancelled = false;
    api
      .get('/maturity-assessment/insights')
      .then((res) => {
        if (cancelled) return;
        const score = res?.data?.overall_score;
        const band = res?.data?.maturity_band;
        if (typeof score === 'number') {
          setOrgScore(score);
          setOrgBandLabel(band || null);
          if (!manualOverride) {
            // Mirror MaturityHealthPage's getBand() thresholds so the
            // two pages stay perfectly aligned.
            const next = score >= 75 ? 'high' : score >= 50 ? 'medium' : 'low';
            setMaturityKey(next);
          }
        }
      })
      .catch(() => {
        // No assessment yet, RBAC block, or other read failure —
        // fall through to the default 'low' band silently. The
        // section is still fully functional as a teaching aid.
      });
    return () => {
      cancelled = true;
    };
    // We intentionally only run this once. `manualOverride` is
    // checked inside the .then() callback at the time of resolution.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectBand = (key) => {
    setManualOverride(true);
    setMaturityKey(key);
  };

  const activeBand = MATURITY_BANDS.find((b) => b.key === maturityKey) ?? MATURITY_BANDS[0];
  const chainValues = computeChainValues(activeBand);
  const realizedRoi = chainValues[chainValues.length - 1].cumulative;
  const lowBand = MATURITY_BANDS[0];
  const highBand = MATURITY_BANDS[MATURITY_BANDS.length - 1];
  const lowRoi = computeChainValues(lowBand).slice(-1)[0].cumulative;
  const highRoi = computeChainValues(highBand).slice(-1)[0].cumulative;
  // The maturity band that mirrors the org's actual score (used to
  // decorate the matching pill with a "your org" badge).
  const orgBandKey =
    typeof orgScore === 'number'
      ? orgScore >= 75
        ? 'high'
        : orgScore >= 50
          ? 'medium'
          : 'low'
      : null;

  // Fire-and-forget download telemetry. Never blocks the link click
  // — the anchor's default navigation runs in parallel, which is
  // fine because the API call is non-blocking and `target=_blank`
  // means the page itself doesn't unmount.
  const trackDownload = (source) => {
    api.post('/analytics/whitepaper/track', {
      whitepaper: 'maturity_governance',
      source,
      page_path: window.location.pathname,
    }).catch(() => {});
  };

  // Separate telemetry for the portable-blueprint developer artifacts
  // so they don't pollute the governance-whitepaper funnel.
  const trackBlueprint = (asset) => {
    api.post('/analytics/whitepaper/track', {
      whitepaper: 'maturity_blueprint',
      source: asset,
      page_path: window.location.pathname,
    }).catch(() => {});
  };

  const __pageTheme = getLightSectionTheme('Navy');
  return (
    <PageShell theme={__pageTheme} hero={(
      <PageHero
        title="Learning Maturity Reference"
        description="Understand the maturity ladder and what each band looks like — from ad-hoc training to a continuously-optimized learning system."
        theme={__pageTheme}
        icon={Layers}
        testId="maturity-reference-hero"
      />
    )}>
    <div
      className="px-4 md:px-8 lg:px-12 py-6 space-y-6"
      data-testid="maturity-reference-page"
    >
      {/* SECTION 1 — Header */}
      <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2 flex-shrink-0">
          <a
            href={GOVERNANCE_WHITEPAPER_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackDownload('header_pill')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-indigo-600 hover:bg-indigo-700 text-white rounded-md transition-colors"
            data-testid="maturity-ref-whitepaper-btn"
          >
            <Download className="h-3.5 w-3.5" />
            Whitepaper
          </a>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(-1)}
            className="gap-1.5"
            data-testid="maturity-ref-back-btn"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
        </div>
      </div>

      <Card className="border-2 border-indigo-100 bg-gradient-to-br from-indigo-50/60 to-white">
        <CardContent className="pt-6 text-sm text-slate-700 leading-relaxed">
          Learning Maturity is the system-level capability that governs how effectively learning translates into workforce performance and financial return. It does not measure how much learning <em>occurs</em> — it measures how much value is <strong>realized</strong>.
        </CardContent>
      </Card>

      {/* SECTION 2 — The Big Picture */}
      <Card data-testid="maturity-big-picture-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-indigo-600" />
            The Big Picture
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm text-slate-700">
          <p>
            Learning impact is not created by training alone — it is created by the system that surrounds it.
          </p>
          <p>
            Learning systems generate <strong>potential</strong> value through improved knowledge and skills. Business systems <strong>capture</strong> value through execution. Learning Maturity is the bridge between the two — ensuring that capability becomes performance.
          </p>
          <div className="p-3 bg-indigo-50 border border-indigo-100 rounded-lg text-indigo-800">
            <strong>Without maturity, learning remains theoretical. With maturity, learning becomes measurable.</strong>
          </div>

          {/* Maturity selector — drives the live multipliers below.
              We render the three bands as a segmented control so it
              reads as a slider without the precision-overhead of an
              actual range input. */}
          <div className="pt-2">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
              <div>
                <p className="text-[12px] uppercase tracking-wider font-semibold text-slate-500">
                  See how maturity reshapes the value chain
                </p>
                {typeof orgScore === 'number' && (
                  <p
                    className="text-[11px] text-indigo-700 mt-0.5"
                    data-testid="maturity-org-hint"
                  >
                    Your org scores <strong>{Math.round(orgScore)}/100</strong>
                    {orgBandLabel ? ` (${orgBandLabel})` : ''} — selector defaults to your band.
                  </p>
                )}
              </div>
              <div
                className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1 gap-1"
                role="tablist"
                aria-label="Maturity level selector"
                data-testid="maturity-band-selector"
              >
                {MATURITY_BANDS.map((band) => {
                  const Icon = band.icon;
                  const isActive = band.key === maturityKey;
                  const isOrgBand = band.key === orgBandKey;
                  return (
                    <button
                      key={band.key}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => handleSelectBand(band.key)}
                      className={cn(
                        'relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all',
                        isActive
                          ? cn(band.activeBg, band.activeText, 'shadow-sm')
                          : 'text-slate-600 hover:text-slate-900 hover:bg-white',
                      )}
                      data-testid={`maturity-band-btn-${band.key}`}
                    >
                      <Icon className="h-3.5 w-3.5" />
                      {band.label}
                      {isOrgBand && (
                        <span
                          className={cn(
                            'ml-1 inline-flex items-center text-[9px] font-bold px-1 py-0.5 rounded uppercase tracking-wider',
                            isActive
                              ? 'bg-white border border-slate-200 text-slate-900'
                              : 'bg-indigo-100 text-indigo-700',
                          )}
                          data-testid={`maturity-band-yours-${band.key}`}
                        >
                          Your org
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Active-band summary banner */}
            <div
              className={cn(
                'flex items-center gap-3 p-3 rounded-lg border',
                activeBand.chipBg,
                activeBand.chipBorder,
              )}
              data-testid="maturity-band-summary"
            >
              <div className={cn('p-2 rounded-md bg-white border border-slate-200', activeBand.chipText)}>
                <activeBand.icon className="h-4 w-4" />
              </div>
              <div className="flex-1">
                <p className={cn('text-sm font-semibold', activeBand.chipText)}>
                  {activeBand.headline}
                </p>
                <p className="text-[11px] text-slate-600 mt-0.5">
                  {activeBand.sublabel} · Maturity factor ≈ {Math.round(activeBand.factor * 100)}%
                </p>
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                  Realized ROI
                </p>
                <p
                  className={cn('text-lg font-bold tabular-nums', activeBand.chipText)}
                  data-testid="maturity-realized-roi"
                >
                  {realizedRoi.toFixed(2)}×
                </p>
              </div>
            </div>
          </div>

          {/* Value Chain visual — animates per-stage retention */}
          <div className="overflow-x-auto pb-2">
            <div
              className="flex items-start gap-1.5 min-w-[760px]"
              data-testid="maturity-value-chain"
            >
              {chainValues.map((step, i, arr) => {
                const isLeak = step.mult !== null && step.mult < 1;
                const isAmp = step.mult !== null && step.mult > 1;
                return (
                  <React.Fragment key={step.label}>
                    <div className="flex flex-col items-center flex-1 min-w-0">
                      {/* Per-stage multiplier chip — sits *above* the
                          stage block so the eye reads "force applied →
                          stage" left-to-right. */}
                      <div className="h-5 mb-1 flex items-center justify-center">
                        {step.mult !== null ? (
                          <span
                            className={cn(
                              'inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full border tabular-nums transition-colors',
                              isLeak && 'bg-rose-50 text-rose-700 border-rose-200',
                              isAmp && 'bg-emerald-50 text-emerald-700 border-emerald-200',
                              !isLeak && !isAmp && 'bg-slate-50 text-slate-600 border-slate-200',
                            )}
                            data-testid={`maturity-stage-mult-${step.label}`}
                          >
                            {isLeak && <TrendingDown className="h-2.5 w-2.5" />}
                            {isAmp && <TrendingUp className="h-2.5 w-2.5" />}
                            ×{step.mult.toFixed(2)}
                          </span>
                        ) : (
                          <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                            Start
                          </span>
                        )}
                      </div>
                      <div
                        className={cn(
                          'w-full rounded-lg py-2 px-1 text-center text-white shadow-sm transition-all duration-300 ring-2',
                          step.color,
                          activeBand.haloRing,
                        )}
                      >
                        <p className="text-xs font-bold">{step.label}</p>
                      </div>
                      <p className="text-[10px] text-slate-500 font-medium mt-1.5 whitespace-pre-line text-center leading-tight">
                        {step.desc}
                      </p>
                      {/* Cumulative running value at this stage */}
                      <p
                        className={cn(
                          'text-[11px] font-bold tabular-nums mt-1',
                          step.mult === null
                            ? 'text-slate-700'
                            : step.cumulative >= 1
                              ? 'text-emerald-700'
                              : step.cumulative >= 0.6
                                ? 'text-amber-700'
                                : 'text-rose-700',
                        )}
                        data-testid={`maturity-stage-cum-${step.label}`}
                      >
                        {step.cumulative.toFixed(2)}×
                      </p>
                    </div>
                    {i < arr.length - 1 && (
                      <div className="flex items-center pt-7 flex-shrink-0">
                        <ArrowRight
                          className={cn(
                            'h-4 w-4 transition-colors',
                            arr[i + 1].mult < 1 && 'text-rose-400',
                            arr[i + 1].mult > 1 && 'text-emerald-400',
                            arr[i + 1].mult === 1 && 'text-slate-300',
                          )}
                        />
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          <p className="text-center text-[12px] text-slate-500 italic">
            Per-stage chips show the multiplier applied at each step. Bottom number is the cumulative value retained from $1 invested.
          </p>

          {/* Static side-by-side: leakage vs amplification.
              Anchors the interactive story with a fixed "what's at
              stake" snapshot — independent of the active selection. */}
          <div
            className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2"
            data-testid="maturity-leak-vs-amp"
          >
            <div className="p-4 rounded-lg border-2 border-rose-200 bg-gradient-to-br from-rose-50 to-white">
              <div className="flex items-center gap-2 mb-2">
                <Droplet className="h-4 w-4 text-rose-600" />
                <p className="text-sm font-bold text-rose-800">Low Maturity → Leakage</p>
              </div>
              <p className="text-[12px] text-rose-700/90 leading-relaxed">
                Each stage loses value: weak engagement, broken transfer, untracked KPIs. Most potential return never reaches the business.
              </p>
              <div className="mt-3 flex items-baseline gap-2">
                <span className="text-2xl font-bold text-rose-700 tabular-nums">
                  {lowRoi.toFixed(2)}×
                </span>
                <span className="text-[11px] text-rose-600 font-medium">
                  realized of potential
                </span>
              </div>
              <div className="mt-1.5 h-1.5 rounded-full bg-rose-100 overflow-hidden">
                <div
                  className="h-full bg-rose-500 transition-all"
                  style={{ width: `${Math.min(100, lowRoi * 100)}%` }}
                />
              </div>
            </div>
            <div className="p-4 rounded-lg border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="h-4 w-4 text-emerald-600" />
                <p className="text-sm font-bold text-emerald-800">High Maturity → Amplification</p>
              </div>
              <p className="text-[12px] text-emerald-700/90 leading-relaxed">
                Aligned governance, strong transfer, instrumented KPIs. Each stage compounds the previous one — value grows beyond initial spend.
              </p>
              <div className="mt-3 flex items-baseline gap-2">
                <span className="text-2xl font-bold text-emerald-700 tabular-nums">
                  {highRoi.toFixed(2)}×
                </span>
                <span className="text-[11px] text-emerald-600 font-medium">
                  realized of potential
                </span>
              </div>
              <div className="mt-1.5 h-1.5 rounded-full bg-emerald-100 overflow-hidden">
                <div
                  className="h-full bg-emerald-500 transition-all"
                  style={{ width: `${Math.min(100, highRoi * 100)}%` }}
                />
              </div>
            </div>
          </div>

          <div className="text-center text-[12px] text-slate-500 italic">
            Learning Maturity acts as the <strong className="text-indigo-700 not-italic">multiplier</strong> across every stage of this chain.
          </div>
        </CardContent>
      </Card>

      {/* SECTION 3 — What it measures (5 pillars) */}
      <div className="space-y-3">
        <h2 className="text-lg font-semibold text-slate-900">
          What Learning Maturity Measures
        </h2>
        <p className="text-sm text-slate-600">
          Assessed across five core pillars (weight = relative importance in the composite score):
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="maturity-pillars-grid">
          {PILLARS.map((p) => {
            const a = ACCENT_CLASSES[p.accent];
            const Icon = p.icon;
            return (
              <Card
                key={p.name}
                className={cn('border-l-4', a.border)}
                data-testid={`maturity-pillar-${p.accent}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className={cn('p-1.5 rounded-md', a.bg)}>
                        <Icon className={cn('h-4 w-4', a.text)} />
                      </span>
                      <CardTitle className="text-base">{p.name}</CardTitle>
                    </div>
                    <span className={cn('text-[11px] font-semibold px-2 py-0.5 rounded-full', a.badge)}>
                      {p.weight}
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="text-sm text-slate-700 space-y-2">
                  <p>{p.summary}</p>
                  <div className="text-[12px] text-slate-500 border-l-2 border-slate-200 pl-2">
                    <strong className="text-slate-700">Why it matters:</strong> {p.why}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* SECTION 4 — Maturity Levels */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-indigo-600" />
            Maturity Levels
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-slate-600">
            Your maturity score reflects how effectively your learning ecosystem converts investment into impact.
          </p>
          <div className="space-y-2" data-testid="maturity-levels-list">
            {LEVELS.map((lv) => (
              <div
                key={lv.band}
                className="flex items-start gap-3 p-3 border border-slate-200 rounded-lg bg-white"
                data-testid={`maturity-level-${lv.band.toLowerCase()}`}
              >
                <div className={cn('w-16 shrink-0 rounded-md py-2 text-center text-white text-[11px] font-bold', lv.color)}>
                  <div>{lv.band}</div>
                  <div className="text-[10px] opacity-90 mt-0.5">{lv.range}</div>
                </div>
                <div className="flex-1 text-sm">
                  <p className="text-slate-800">{lv.summary}</p>
                  <p className="text-[12px] text-slate-500 mt-1">
                    <strong className="text-rose-700">Risk:</strong> {lv.risk}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* SECTION 5 — ROI Leakage */}
      <Card className="border-2 border-amber-100 bg-gradient-to-br from-amber-50/40 to-white">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            Understanding ROI Leakage
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm text-slate-700">
          <p>
            Most organizations do not lose value because of poor learning — they lose value because of poor <strong>conversion</strong>.
          </p>
          <p>
            <strong>ROI Leakage</strong> is the portion of potential value that is never realized due to gaps in maturity.
          </p>

          <div className="bg-white border border-amber-200 rounded-lg p-4 text-center" data-testid="maturity-roi-formula">
            <p className="text-xl font-mono font-bold text-amber-900">
              Potential ROI × Maturity Factor = Realized ROI
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="p-3 border border-slate-200 rounded-lg bg-white text-center">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Potential ROI</p>
              <p className="text-lg font-bold text-slate-800 mt-1">3.0x</p>
            </div>
            <div className="p-3 border border-amber-200 rounded-lg bg-amber-50 text-center">
              <p className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">× Maturity</p>
              <p className="text-lg font-bold text-amber-700 mt-1">60%</p>
            </div>
            <div className="p-3 border border-emerald-200 rounded-lg bg-emerald-50 text-center">
              <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">= Realized ROI</p>
              <p className="text-lg font-bold text-emerald-700 mt-1">1.8x</p>
            </div>
          </div>

          <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg text-indigo-800">
            <strong>Key insight:</strong> Increasing maturity does not require more learning investment — it improves the return on what already exists.
          </div>
        </CardContent>
      </Card>

      {/* SECTION 6 — Where maturity impacts the system */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-indigo-600" />
            Where Maturity Impacts Seertech
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-slate-600">
            Learning Maturity influences every stage of the value chain:
          </p>
          <div className="space-y-2" data-testid="maturity-stage-impact-list">
            {STAGE_IMPACT.map((s) => (
              <div
                key={s.stage}
                className={cn(
                  'flex items-start gap-3 p-3 border rounded-lg',
                  s.isLargest ? 'border-teal-300 bg-teal-50/60' : 'border-slate-200 bg-white',
                )}
              >
                <div
                  className={cn(
                    'w-14 shrink-0 rounded-md py-1.5 text-center text-white text-xs font-bold',
                    s.isLargest ? 'bg-teal-600' : 'bg-slate-600',
                  )}
                >
                  {s.stage}
                </div>
                <div className="flex-1 text-sm">
                  <p className="font-semibold text-slate-800">{s.title}</p>
                  <p className="text-[12px] text-slate-600 mt-0.5">{s.impact}</p>
                </div>
                {s.isLargest && (
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-teal-100 text-teal-700 self-center">
                    Largest leak
                  </span>
                )}
              </div>
            ))}
          </div>
          <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg text-sm text-teal-800">
            <strong>Key insight:</strong> The greatest impact of maturity is at the <strong>transfer layer (ROE)</strong> — where capability becomes performance.
          </div>
        </CardContent>
      </Card>

      {/* SECTION 7 — Primary Constraint */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Compass className="h-5 w-5 text-rose-600" />
            Understanding Your Primary Constraint
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-slate-700">
          <p>
            Your overall maturity is determined by your <strong>weakest pillar</strong>.
          </p>
          <p className="text-slate-500 italic">
            Even strong performance in multiple areas cannot compensate for a critical gap in one.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {[
              'Weak governance disrupts alignment',
              'Weak measurement limits visibility',
              'Weak operations reduce scale',
              'Weak engagement reduces reach',
            ].map((item) => (
              <div
                key={item}
                className="flex items-start gap-2 text-[12px] p-2 bg-rose-50 border border-rose-100 rounded"
              >
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 text-rose-500 shrink-0" />
                <span className="text-rose-800">{item}</span>
              </div>
            ))}
          </div>
          <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg text-rose-800">
            <strong>Key insight:</strong> Improving your primary constraint delivers the highest ROI uplift.
          </div>
        </CardContent>
      </Card>

      {/* SECTION 8 — How to Improve */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-emerald-600" />
            How to Improve Learning Maturity
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-600">
            Improving maturity is not about doing more — it is about fixing where value is lost.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="maturity-improve-steps">
            {IMPROVE_STEPS.map((s) => (
              <div
                key={s.n}
                className="flex items-start gap-3 p-3 border border-slate-200 rounded-lg bg-white"
              >
                <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-sm shrink-0">
                  {s.n}
                </div>
                <div className="text-sm">
                  <p className="font-semibold text-slate-800">{s.title}</p>
                  <p className="text-[12px] text-slate-600 mt-0.5">{s.body}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800">
            <strong>Key insight:</strong> Maturity improvement compounds — small structural changes create exponential impact over time.
          </div>
        </CardContent>
      </Card>

      {/* SECTION 9 — Key Takeaways */}
      <Card className="bg-gradient-to-br from-indigo-50 to-white border-2 border-indigo-100">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-indigo-600" />
            Key Takeaways
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2" data-testid="maturity-takeaways-list">
            {TAKEAWAYS.map((t) => (
              <li key={t} className="flex items-start gap-2 text-sm text-slate-700">
                <CheckCircle2 className="h-4 w-4 text-indigo-600 mt-0.5 shrink-0" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {/* Whitepaper CTA — matches the optional "Download" section
          described in the source spec. Larger, more prominent than
          the header pill. */}
      <Card
        className="border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-white"
        data-testid="maturity-ref-whitepaper-card"
      >
        <CardContent className="p-5 flex items-start gap-4">
          <div className="p-3 rounded-lg bg-indigo-600 text-white shrink-0">
            <BookOpen className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-indigo-900">
              Learning Governance Whitepaper
            </p>
            <p className="text-[12px] text-slate-600 mt-1 leading-relaxed">
              The full L&D strategy guide on building an effective
              learning content governance framework — covering
              governance pillars, ROI leakage, maturity benchmarks,
              and practical steps to lift your maturity score.
            </p>
            <a
              href={GOVERNANCE_WHITEPAPER_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackDownload('cta_card')}
              className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-indigo-600 hover:bg-indigo-700 text-white rounded-md transition-colors"
              data-testid="maturity-ref-whitepaper-cta"
            >
              <Download className="h-3.5 w-3.5" />
              Download PDF
            </a>
          </div>
        </CardContent>
      </Card>

      {/* Portable Blueprint — developer extraction artifacts. Lets a
          builder rebuild this entire Maturity module in another
          Emergent app: a self-contained spec (.md) + a one-command
          Mongo seeder (.py). Served statically from public/downloads. */}
      <Card
        className="border-2 border-slate-200 bg-gradient-to-br from-slate-50 to-white"
        data-testid="maturity-ref-blueprint-card"
      >
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Code2 className="h-5 w-5 text-slate-700" />
            Portable Blueprint
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-200 text-slate-700 uppercase tracking-wider">
              For builders
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-600 leading-relaxed">
            A self-contained extraction of the entire Learning Maturity
            module — the 5-pillar / 30-dimension questionnaire, scoring
            math, band insights, action playbooks, AI prompts, Mongo
            schema, and REST surface — so it can be rebuilt in another
            app. Pair the spec with the one-command seeder to bootstrap
            the framework in minutes.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="maturity-blueprint-downloads">
            <a
              href="/downloads/MATURITY_BLUEPRINT.md"
              download="MATURITY_BLUEPRINT.md"
              onClick={() => trackBlueprint('blueprint_md')}
              className="flex items-start gap-3 p-4 rounded-lg border border-slate-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/40 transition-colors group"
              data-testid="maturity-blueprint-md-download"
            >
              <div className="p-2 rounded-md bg-indigo-100 text-indigo-700 shrink-0 group-hover:bg-indigo-600 group-hover:text-white transition-colors">
                <FileText className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
                  Blueprint spec
                  <Download className="h-3.5 w-3.5 text-slate-400 group-hover:text-indigo-600 transition-colors" />
                </p>
                <p className="text-[12px] text-slate-500 mt-0.5">
                  MATURITY_BLUEPRINT.md — full framework, scoring, schema & port checklist.
                </p>
              </div>
            </a>
            <a
              href="/downloads/maturity_blueprint_importer.py"
              download="maturity_blueprint_importer.py"
              onClick={() => trackBlueprint('importer_py')}
              className="flex items-start gap-3 p-4 rounded-lg border border-slate-200 bg-white hover:border-emerald-300 hover:bg-emerald-50/40 transition-colors group"
              data-testid="maturity-blueprint-py-download"
            >
              <div className="p-2 rounded-md bg-emerald-100 text-emerald-700 shrink-0 group-hover:bg-emerald-600 group-hover:text-white transition-colors">
                <Code2 className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
                  Importer script
                  <Download className="h-3.5 w-3.5 text-slate-400 group-hover:text-emerald-600 transition-colors" />
                </p>
                <p className="text-[12px] text-slate-500 mt-0.5">
                  maturity_blueprint_importer.py — idempotent one-command Mongo seeder.
                </p>
              </div>
            </a>
          </div>
          <div className="flex items-stretch gap-2" data-testid="maturity-blueprint-command">
            <div className="flex-1 p-3 bg-slate-100 border border-slate-200 rounded-lg text-[12px] text-slate-600 font-mono overflow-x-auto whitespace-nowrap">
              cd backend &amp;&amp; python scripts/maturity_blueprint_importer.py
            </div>
            <button
              type="button"
              onClick={handleCopyCommand}
              className={cn(
                'shrink-0 inline-flex items-center gap-1.5 px-3 rounded-lg text-xs font-medium border transition-colors',
                copiedCmd
                  ? 'bg-emerald-600 border-emerald-600 text-white'
                  : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50',
              )}
              data-testid="maturity-blueprint-copy-command"
              aria-label="Copy seeder command to clipboard"
            >
              {copiedCmd ? (
                <>
                  <Check className="h-3.5 w-3.5" /> Copied
                </>
              ) : (
                <>
                  <Copy className="h-3.5 w-3.5" /> Copy
                </>
              )}
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Cross-references — shared component */}
      <RelatedReferences
        activeRef="maturity"
        extras={[{ href: '/maturity-health', label: 'Maturity Health' }]}
      />
    </div>
    </PageShell>
  );
}
